import { UserButton, useAuth, useUser } from "@clerk/clerk-react";
import { ApiError } from "@easyshot/shared";
import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { AdminNav } from "@/components/admin-nav";
import { CreditsBadge } from "@/components/credits-badge";
import { api } from "@/lib/api";
import { Logo } from "@/components/site-chrome";
import { StudioNav } from "@/components/studio-nav";

const clerkEnabled = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

export function StudioLayout() {
  return clerkEnabled ? <AuthenticatedStudioLayout /> : <StudioShell />;
}

function AuthenticatedStudioLayout() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <StudioShell email={user?.primaryEmailAddress?.emailAddress || null} />;
}

function StudioShell({ email = null }: { email?: string | null }) {
  return (
    <div className="flex min-h-full flex-1 flex-col lg:flex-row">
      <aside className="border-b border-line lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-60 lg:shrink-0 lg:flex-col lg:border-r lg:border-b-0">
        <div className="flex h-16 items-center px-5"><Logo size="sm" /></div>
        <StudioNav />
        <div className="hidden px-4 lg:block">
          <CreditsBadge showAccount={clerkEnabled} email={email} />
        </div>
      </aside>
      <main className="relative min-w-0 flex-1">
        {clerkEnabled && <div className="absolute top-5 right-5 z-40"><UserButton /></div>}
        <Outlet />
      </main>
    </div>
  );
}

export function AdminLayout() {
  return clerkEnabled ? <AuthenticatedAdminLayout /> : <AdminShell />;
}

type AdminAccess = "checking" | "allowed" | "forbidden" | "error";
type AdminAccessState = { status: AdminAccess; userId: string | null };

function AuthenticatedAdminLayout() {
  const { getToken, isLoaded, isSignedIn, userId } = useAuth();
  const [access, setAccess] = useState<AdminAccessState>({
    status: "checking",
    userId: null,
  });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    let active = true;
    const verifyingUserId = userId;
    setAccess({ status: "checking", userId: verifyingUserId });

    const verify = async () => {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const token = await getToken();
          const me = await api.me(token ?? undefined, { includeAdmin: true });
          if (active) {
            setAccess({
              status: me.isAdmin ? "allowed" : "forbidden",
              userId: verifyingUserId,
            });
          }
          return;
        } catch (error) {
          const sessionMayStillBeSyncing =
            error instanceof ApiError && error.status === 401 && attempt < 2;
          if (sessionMayStillBeSyncing) {
            await new Promise((resolve) =>
              window.setTimeout(resolve, 200 * (attempt + 1)),
            );
            if (!active) return;
            continue;
          }
          if (active) {
            setAccess({
              status:
                error instanceof ApiError && error.status === 403
                  ? "forbidden"
                  : "error",
              userId: verifyingUserId,
            });
          }
          return;
        }
      }
    };

    void verify();
    return () => {
      active = false;
    };
  }, [getToken, isLoaded, isSignedIn, retryKey, userId]);

  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  if (!userId || access.userId !== userId || access.status === "checking") {
    return null;
  }
  if (access.status === "forbidden") {
    return <Navigate to="/studio" replace />;
  }
  if (access.status === "error") {
    return (
      <main className="grid min-h-screen place-items-center px-6">
        <div className="card max-w-md p-6 text-center">
          <h1 className="text-lg font-semibold">Unable to verify admin access</h1>
          <p className="mt-2 text-sm text-muted">
            Your session is signed in, but the server-side permission check did
            not complete.
          </p>
          <button
            type="button"
            className="btn-primary mt-5"
            onClick={() => setRetryKey((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      </main>
    );
  }
  return <AdminShell />;
}

function AdminShell() {
  return (
    <div className="flex min-h-full flex-1 flex-col lg:flex-row">
      <aside className="border-b border-line lg:sticky lg:top-0 lg:h-screen lg:w-60 lg:shrink-0 lg:border-r lg:border-b-0">
        <div className="flex h-16 items-center gap-2 px-5">
          <Logo size="sm" />
          <span className="rounded-md bg-[var(--danger)]/15 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-[var(--danger)]">ADMIN</span>
        </div>
        <AdminNav />
        <div className="hidden px-7 pt-6 text-[11px] leading-5 text-white/25 lg:block">
          Internal console.<br /><a href="/studio" className="hover:text-foreground">← Back to studio</a>
        </div>
      </aside>
      <main className="min-w-0 flex-1"><Outlet /></main>
    </div>
  );
}
